import React from "react";
import ListPage from "../../base/ListPage.js";
import APILXD from "../../../http/APILXD.js";
import {
  Modal,
  message,
  Button,
  Space,
  Tooltip,
  Flex,
  Tag,
  Avatar,
  Select,
} from "antd";
import TimeHelp from "../../../tool/TimeHelp";
import HttpTool from "../../../tool/HttpTool";
import dayjs from "dayjs";
import ProForm, {
  ProFormDependency,
  ProFormDigit,
  ProFormText,
  ProFormTextArea,
} from "@ant-design/pro-form";
import ProTable from "@ant-design/pro-table";
import styles from "./ModelList.less";
import UploadFile from "../../../components/upLoad/UploadFile";
import {
  buildDeleteModelKeyEntityPayload,
  buildDeleteModelKeyPayload,
  buildModelKeyEntityPayload,
  buildModelKeyListPayload,
  buildModelGroupPayload,
  buildChargeRuleText,
  buildModelSubsidyText,
  getModelDisplayName,
  getModelFormInitialValues,
  getModelGroupFormValues,
  getModelKeyListColumnKeys,
  getModelMainListColumnKeys,
  getModelLogoUrl,
  getModelPageAfterDelete,
  getModelSubmitterLoading,
  getModelStatusText,
  getModelListActionTypes,
  MODEL_MODAL_WIDTH,
  MODEL_TYPE_INPUT_STYLE,
  getModelTypeOptions,
  getModelTypeText,
  getNextModelEnableStatus,
  isModelEnabled,
  getModelTypeSearchOptions,
  normalizeModelListSearchParams,
  resolveSelectedModelGroup,
  normalizeModelKeyPayload,
  sumModelKeyTokens,
  CHARGE_RULE_TEXTAREA_STYLE,
} from "./modelListUtils";

function ModelTypeSelectField({
  value,
  onChange,
  modelGroups,
  onModelTypeChange,
  onAddModelType,
}) {
  const [availableGroups, setAvailableGroups] = React.useState(
    modelGroups || []
  );

  const handleChange = (nextValue) => {
    onChange?.(nextValue);
    onModelTypeChange?.(nextValue, availableGroups);
  };

  const handleCreated = (nextGroups, createdGroup) => {
    let groups = Array.isArray(nextGroups) ? nextGroups : [];
    if (
      createdGroup?.modelType &&
      !groups.some((item) => item?._id === createdGroup._id)
    ) {
      groups = [...groups, createdGroup];
    }
    setAvailableGroups(groups);
    if (createdGroup?.modelType) {
      onChange?.(createdGroup.modelType);
      onModelTypeChange?.(createdGroup.modelType, groups);
    }
  };

  return (
    <Space.Compact style={{ width: "100%" }}>
      <Select
        allowClear
        showSearch
        style={{ ...MODEL_TYPE_INPUT_STYLE, flex: 1 }}
        popupMatchSelectWidth
        options={getModelTypeOptions(value, availableGroups)}
        placeholder="请选择模型类型"
        value={value}
        onChange={handleChange}
        filterOption={(inputValue, option) => {
          const text = `${option?.label || ""}${option?.value || ""}`;
          return text.toLowerCase().includes(inputValue.toLowerCase());
        }}
      />
      <Button
        type="primary"
        onClick={() => {
          onAddModelType?.(handleCreated, availableGroups);
        }}
      >
        新增类型
      </Button>
    </Space.Compact>
  );
}

export default class ModelListPage extends ListPage {
  constructor(props) {
    super(props);
  }

  base_getListConfig() {
    return {
      table: {
        columns: this.getTableColumns(),
        url: APILXD.modelKeysList,
        otherParam: null,
        afterForNet: () => {
          this.refreshModelTokenSummary();
        },
      },
      new: {
        defaultValue: "新建模型",
        action: () => {
          this.add();
        },
      },

      search: {
        seniorSearch: this.getSeniorSearch(),
        option: {
          openSenior: true,
          hideSeniorBtn: true,
          hideInput: true,
          placeholder: "",
          reg: /^[\s\S]*$/,
          beforeSubmit: (value) => {
            Object.assign(value, normalizeModelListSearchParams(value));
            if (value.createTime) {
              value.startTime = dayjs(value.createTime[0]).valueOf();
              value.endTime = dayjs(value.createTime[1]).valueOf();
            }
            return true;
          },
        },
      },
    };
  }

  add(data) {
    this.fetchModelGroups()
      .then((modelGroups) => {
        this.openModelModal(data, modelGroups);
      })
      .catch(() => {
        this.openModelModal(data, []);
      });
  }

  requestModelManage(url, param) {
    return new Promise((resolve, reject) => {
      HttpTool.post(
        url,
        (code, msg, json, option) => {
          resolve({ code, msg, json, option });
        },
        (code, msg) => {
          reject(new Error(msg || "操作失败"));
        },
        param
      );
    });
  }

  refreshModelTokenSummary() {
    const rows = Array.isArray(this.items) ? this.items : [];
    if (!rows.length) {
      return;
    }
    const requestId = (this.modelTokenSummaryRequestId || 0) + 1;
    this.modelTokenSummaryRequestId = requestId;

    Promise.all(
      rows.map((record) => {
        if (!record?._id) {
          return Promise.resolve({
            modelId: record?._id,
            tokens: record?.tokens,
          });
        }
        return this.requestModelManage(
          APILXD.modelGetKeyList,
          buildModelKeyListPayload(record._id)
        )
          .then(({ json }) => ({
            modelId: record._id,
            tokens: sumModelKeyTokens(json),
          }))
          .catch(() => ({
            modelId: record._id,
            tokens: record?.tokens,
          }));
      })
    ).then((summaryList) => {
      if (this.modelTokenSummaryRequestId !== requestId) {
        return;
      }
      const tokenMap = new Map(
        summaryList.map((item) => [item.modelId, item.tokens])
      );
      this.items = rows.map((record) => ({
        ...record,
        tokens: tokenMap.has(record._id)
          ? tokenMap.get(record._id)
          : record.tokens,
      }));
      this.setState({
        tokenSummaryVersion: requestId,
      });
    });
  }

  fetchModelGroups(modelType) {
    const param = {
      page: 1,
      pageSize: 500,
    };
    if (modelType) {
      param.modelType = modelType;
    }
    return this.requestModelManage(APILXD.getAiModelList, param).then(
      ({ json }) => {
        return Array.isArray(json) ? json : [];
      }
    );
  }

  findModelGroup(modelGroups, modelType) {
    return (modelGroups || []).find((item) => item?.modelType === modelType);
  }

  ensureModelGroup(
    values,
    modelGroups,
    fallbackParentModelId,
    fallbackModelType
  ) {
    const selectedGroup = resolveSelectedModelGroup(
      values,
      modelGroups,
      fallbackParentModelId,
      fallbackModelType
    );
    if (selectedGroup?._id) {
      return Promise.resolve(selectedGroup);
    }

    return Promise.reject(new Error("请选择有效的模型类型"));
  }

  openModelTypeModal(onCreated, currentModelGroups = []) {
    const typeModal = Modal.confirm({
      title: "新增模型类型",
      icon: null,
      width: 500,
      okText: "添加",
      cancelText: "取消",
      closable: true,
      content: (
        <div>
          <ProForm
            layout="horizontal"
            labelCol={{ flex: "92px" }}
            wrapperCol={{ flex: 1 }}
            submitter={{
              render: (props) => {
                const submitting = getModelSubmitterLoading(props);
                return (
                  <Flex style={{ width: "100%" }} justify="flex-end">
                    <Space size={16}>
                      <Button
                        type="default"
                        disabled={submitting}
                        onClick={() => {
                          typeModal.destroy();
                        }}
                      >
                        取消
                      </Button>
                      <Button
                        type="primary"
                        loading={submitting}
                        onClick={() => {
                          props.form?.submit();
                        }}
                      >
                        保存
                      </Button>
                    </Space>
                  </Flex>
                );
              },
            }}
            onFinish={(values) => {
              return new Promise((resolve, reject) => {
                this.requestModelManage(
                  APILXD.addAiModelGroup,
                  buildModelGroupPayload(values)
                )
                  .then(() => this.fetchModelGroups())
                  .then((nextGroups) => {
                    const createdGroup = this.findModelGroup(
                      nextGroups,
                      values.modelType
                    );
                    if (!createdGroup?._id) {
                      throw new Error("模型类型创建成功，但未获取到分类ID");
                    }
                    message.success("类型添加成功");
                    onCreated?.(nextGroups, createdGroup);
                    resolve(true);
                    typeModal.destroy();
                  })
                  .catch((err) => {
                    message.error(err.message || "操作失败");
                    reject(false);
                  });
              });
            }}
          >
            <ProFormText
              label="模型类型"
              placeholder="请输入模型类型"
              name="modelType"
              fieldProps={{
                maxLength: 30,
              }}
              rules={[
                { required: true, message: "请输入模型类型" },
                {
                  pattern: /^[a-zA-Z0-9\u4e00-\u9fa5]+$/,
                  message: "模型类型只支持数字、字母、中文",
                },
                {
                  validator: (_, value) => {
                    if (
                      value &&
                      currentModelGroups.some(
                        (item) => item?.modelType === value
                      )
                    ) {
                      return Promise.reject(new Error("模型类型已存在"));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            />
            <ProFormText
              label="编码"
              placeholder="请输入编码"
              name="bm"
              rules={[
                { required: true, message: "请输入编码" },
                {
                  pattern: /^[a-zA-Z0-9]+$/,
                  message: "编码只支持数字和字母",
                },
              ]}
              fieldProps={{
                maxLength: 60,
              }}
            />
          </ProForm>
        </div>
      ),
      footer: null,
    });
  }

  openModelModal(data, modelGroups = []) {
    const formRef = React.createRef();
    let currentModelGroups = modelGroups;
    const syncModelGroupFields = (modelType, nextModelGroups) => {
      const groups = nextModelGroups || currentModelGroups;
      const groupValues = getModelGroupFormValues(groups, modelType);
      if (groupValues) {
        formRef.current?.setFieldsValue({
          modelType,
          ...groupValues,
        });
      } else {
        formRef.current?.setFieldsValue({
          modelType,
          bm: "",
        });
      }
    };
    const modelModal = Modal.confirm({
      title: data ? "编辑模型" : "新建模型",
      icon: null,
      width: MODEL_MODAL_WIDTH,
      okText: data ? "修改" : "添加",
      cancelText: "取消",
      closable: true,
      content: (
        <div>
          <ProForm
            formRef={formRef}
            layout="horizontal"
            labelCol={{ flex: "116px" }}
            wrapperCol={{ flex: 1 }}
            initialValues={getModelFormInitialValues(data)}
            submitter={{
              render: (props) => {
                const submitting = getModelSubmitterLoading(props);
                return (
                  <Flex style={{ width: "100%" }} justify="flex-end">
                    <Space size={16}>
                      <Button
                        type="default"
                        disabled={submitting}
                        onClick={() => {
                          modelModal.destroy();
                        }}
                      >
                        取消
                      </Button>
                      <Button
                        type="primary"
                        loading={submitting}
                        onClick={() => {
                          props.form?.submit();
                        }}
                      >
                        保存
                      </Button>
                    </Space>
                  </Flex>
                );
              },
            }}
            onFinish={(values) => {
              return new Promise((resolve, reject) => {
                this.ensureModelGroup(
                  values,
                  currentModelGroups,
                  data?.parentModelId,
                  data?.modelType
                )
                  .then((modelGroup) => {
                    const modelPayload = normalizeModelKeyPayload({
                      ...values,
                      enableStatus: data?.enableStatus ?? values.enableStatus,
                    });
                    const param = data
                      ? {
                        _id: data._id,
                        parentModelId: modelGroup._id,
                        ...modelPayload,
                      }
                      : { parentModelId: modelGroup._id, ...modelPayload };
                    return this.requestModelManage(
                      data ? APILXD.modelEditKey : APILXD.modelAddKey,
                      param
                    );
                  })
                  .then(({ code, msg, json }) => {
                    console.log("ai model create", code, msg, json);
                    message.success(data ? "修改成功" : "添加成功");
                    this._loadDataForNet();
                    resolve(true);
                    modelModal.destroy();
                  })
                  .catch((err) => {
                    message.error(err.message || "操作失败");
                    reject(false);
                  });
              });
            }}
          >
            <ProFormText
              label="模型名称"
              placeholder="请输入"
              name="modelName"
              fieldProps={{
                maxLength: 30,
              }}
              rules={[{ required: true, message: "请输入模型名称" }]}
            />
            <ProForm.Item
              label="模型类型"
              name="modelType"
              rules={[{ required: true, message: "请选择模型类型" }, {
                pattern: /^[a-zA-Z0-9\u4e00-\u9fa5]+$/,
                message: "模型类型只支持数字、字母、中文",
              },]}
            >
              <ModelTypeSelectField
                modelGroups={currentModelGroups}
                onModelTypeChange={syncModelGroupFields}
                onAddModelType={(handleCreated, availableGroups) => {
                  this.openModelTypeModal((nextGroups, createdGroup) => {
                    currentModelGroups = nextGroups;
                    handleCreated(nextGroups, createdGroup);
                  }, availableGroups);
                }}
              />
            </ProForm.Item>
            <ProFormText
              label="编码"
              placeholder="选择模型类型后自动填充"
              name="bm"
              rules={[
                { required: true, message: "请输入编码" },
                {
                  pattern: /^[a-zA-Z0-9]+$/,
                  message: "编码只支持数字和字母",
                },
              ]}
              fieldProps={{
                maxLength: 60,
                disabled: true,
              }}
            />
            <ProForm.Item
              label="模型logo"
              name="modelLogo"
              normalize={(value) => {
                return value?.[0]?.url || null;
              }}
              rules={[
                {
                  required: true,
                  message: '请上传模型logo',
                },
              ]}
            >
              <UploadFile
                type={1}
                accept={"image/*"}
                size={1}
                data={{
                  option: {
                    size: 1,
                    defaultValue: getModelLogoUrl(data)
                      ? [{ url: getModelLogoUrl(data) }]
                      : [],
                  },
                }}
              />
            </ProForm.Item>
            <ProFormTextArea
              label="备注"
              placeholder="请输入"
              name="description"
              fieldProps={{
                showCount: true,
                maxLength: 300,
                autoSize: { minRows: 1, maxRows: 2 },
              }}
            />
            <ProFormDigit
              label="排序"
              placeholder="请输入排序"
              name="sort"
              fieldProps={{
                min: 1,
                precision: 0,
                style: { width: "100%" },
              }}
            />
            <ProFormDigit
              label="官方输入价格"
              placeholder="请输入官方输入价格"
              name="officialInputPrice"
              fieldProps={{
                min: 0,
                precision: 6,
                addonAfter: "元/百万token",
              }}
            />
            <ProFormDigit
              label="官方输出价格"
              placeholder="请输入官方输出价格"
              name="officialOutputPrice"
              fieldProps={{
                min: 0,
                precision: 6,
                addonAfter: "元/百万token",
              }}
            />
            <ProFormDependency name={["inputPrice", "officialInputPrice"]}>
              {({ inputPrice, officialInputPrice }) => {
                return (
                  <ProForm.Item label="平台输入价格" required>
                    <Space align="center">
                      <ProFormDigit
                        noStyle
                        placeholder="请输入平台输入价格"
                        name="inputPrice"
                        fieldProps={{
                          min: 0,
                          precision: 6,
                          addonAfter: "元/百万token",
                          style: { width: 390 },
                        }}
                        rules={[
                          { required: true, message: "请输入平台输入价格" },
                        ]}
                      />
                      <Tag color="green">
                        {buildModelSubsidyText(inputPrice, officialInputPrice)}
                      </Tag>
                    </Space>
                  </ProForm.Item>
                );
              }}
            </ProFormDependency>
            <ProFormDependency name={["outputPrice", "officialOutputPrice"]}>
              {({ outputPrice, officialOutputPrice }) => {
                return (
                  <ProForm.Item label="平台输出价格" required>
                    <Space align="center">
                      <ProFormDigit
                        noStyle
                        placeholder="请输入平台输出价格"
                        name="outputPrice"
                        fieldProps={{
                          min: 0,
                          precision: 6,
                          addonAfter: "元/百万token",
                          style: { width: 390 },
                        }}
                        rules={[
                          { required: true, message: "请输入平台输出价格" },
                        ]}
                      />
                      <Tag color="green">
                        {buildModelSubsidyText(
                          outputPrice,
                          officialOutputPrice
                        )}
                      </Tag>
                    </Space>
                  </ProForm.Item>
                );
              }}
            </ProFormDependency>
            <ProFormText
              label="补贴标签"
              placeholder="请输入补贴标签"
              name="discount"
              fieldProps={{
                maxLength: 30,
              }}
            />
            <ProFormText
              label="补贴标签（英文）"
              placeholder="请输入补贴标签"
              name="discountEn"
              fieldProps={{
                maxLength: 50,
              }}
            />
            <ProFormDependency name={["inputPrice", "outputPrice"]}>
              {({ inputPrice, outputPrice }) => {
                return (
                  <ProFormTextArea
                    label="收费规则展示"
                    name="chargeRuleDisplay"
                    disabled
                    fieldProps={{
                      value: buildChargeRuleText(inputPrice, outputPrice),
                      style: CHARGE_RULE_TEXTAREA_STYLE
                    }}
                  />
                );
              }}
            </ProFormDependency>
          </ProForm>
        </div>
      ),
      footer: null,
    });
  }

  getSeniorSearch() {
    return {
      colCount: 3,
      formItemLayout: {
        labelCol: { span: 6 },
        wrapperCol: { span: 18, offset: 0 },
      },
      parameterArr: [
        {
          field: "modelName",
          name: "模型名称",
          type: "Input",
          reg: (v) => {
            return true;
          },
          option: {
            allowClear: true,
          },
        },
        {
          field: "modelType",
          name: "模型分类",
          type: "select",
          selectType: "value",
          reg: (v) => {
            return true;
          },
          apiConfig: {
            url: APILXD.getAiModelList,
            param: {
              page: 1,
              pageSize: 500,
            },
            fillObject: getModelTypeSearchOptions,
          },
          option: {
            allowClear: true,
            showSearch: true,
            dropdownMatchSelectWidth: false,
            optionFilterProp: "children",
            filterOption: (input, option) => {
              return String(option?.props?.children || "")
                .toLowerCase()
                .indexOf(input.toLowerCase()) >= 0;
            },
          },
          data: getModelTypeSearchOptions(),
        },
        {
          field: "status",
          name: "状态",
          type: "select",
          selectType: "value",
          reg: (v) => {
            return true;
          },
          option: {
            allowClear: true,
          },
          data: [
            {
              title: "启用",
              value: 1,
            },
            {
              title: "停用",
              value: 2,
            },
          ],
        },
      ],
    };
  }

  /**
   *  列表数据配置
   */
  getTableColumns() {
    const visibleColumnKeys = getModelMainListColumnKeys();
    return [
      {
        title: "模型名称",
        dataIndex: "model",
        key: "model",
        width: 200,
        maxWidth: 200,
        ellipsis: true,
        render: (text, record) => {
          return (
            <Space>
              {getModelLogoUrl(record) && <Avatar src={getModelLogoUrl(record)}>
                {getModelDisplayName(record).slice(0, 1)}
              </Avatar>}
              <span>{getModelDisplayName(record)}</span>
            </Space>
          );
        },
      },
      {
        title: "备注",
        dataIndex: "description",
        key: "description",
        width: 200,
        ellipsis: true,
      },
      {
        title: "模型类型",
        dataIndex: "modelType",
        key: "modelType",
        width: 120,
        render: (text) => {
          return <div>{getModelTypeText(text)}</div>;
        },
      },
      {
        title: "编码",
        dataIndex: "bm",
        key: "bm",
        width: 100,
        ellipsis: true,
      },
      {
        title: "排序",
        dataIndex: "sort",
        key: "sort",
        width: 80,
        render: (text) => {
          return <div>{text ?? "-"}</div>;
        },
      },
      {
        title: "已使用Tokens",
        dataIndex: "tokens",
        key: "tokens",
        width: 120,
        render: (text) => {
          return <div>{text ?? "-"}</div>;
        },
      },
      {
        title: "用户使用情况",
        dataIndex: "userIds",
        key: "userIds",
        width: 200,
        render: (text, record) => {
          return (
            <div>
              {record.userDetails?.length > 0 ? (
                <Button size={"small"} type={"link"} onClick={() => { }}>
                  <Tooltip
                    title={
                      <div style={{ maxHeight: 120, overflow: "auto" }}>
                        {record?.userDetails?.map((item, index) => {
                          return (
                            <Tag color="cyan" key={index}>
                              {item.username}{" "}
                              {item?.mobile ? `(${item?.mobile})` : ""}
                            </Tag>
                          );
                        })}
                      </div>
                    }
                  >
                    {record.userIds?.length}人编辑
                  </Tooltip>
                </Button>
              ) : (
                "无"
              )}
            </div>
          );
        },
      },
      {
        title: "状态",
        dataIndex: "enableStatus",
        key: "enableStatus",
        width: 80,
        render: (text) => {
          const enabled = isModelEnabled(text);
          return (
            <span style={{ color: enabled ? "limegreen" : "red" }}>
              {getModelStatusText(text)}
            </span>
          );
        },
      },
      {
        title: "更新时间",
        dataIndex: "updateTime",
        key: "updateTime",
        width: 120,
        render: (text, record) => {
          let time = TimeHelp.getYMDHMS(text || record.createTime);
          return <div>{time}</div>;
        },
      },
      {
        title: "操作",
        key: "action",
        width: 200,
        render: (text, record, index) => {
          return (
            <div>{this.getActionByStatusType(record.enableStatus, record)}</div>
          );
        },
      },
    ].filter((column) => visibleColumnKeys.includes(column.key));
  }

  getActionByStatusType(statusType, record) {
    const enabled = isModelEnabled(statusType);
    const actionTypes = getModelListActionTypes(statusType);
    return (
      <Space>
        {actionTypes.includes("key") && (
          <Button
            size={"small"}
            type={"primary"}
            onClick={() => {
              this.openKeyList(record);
            }}
          >
            KEY
          </Button>
        )}
        {actionTypes.includes("edit") && (
          <Button
            size={"small"}
            type={"primary"}
            onClick={() => {
              this.add(record);
            }}
          >
            编辑
          </Button>
        )}
        {actionTypes.includes("toggle") && (
          <Button
            size={"small"}
            type={"primary"}
            danger={enabled}
            onClick={() => {
              this.changeState(record);
            }}
          >
            {enabled ? "停用" : "启用"}
          </Button>
        )}
        {actionTypes.includes("delete") && (
          <Button
            size={"small"}
            type={"primary"}
            danger={true}
            onClick={() => {
              this.delAiModel(record);
            }}
          >
            删除
          </Button>
        )}
      </Space>
    );
  }

  delAiModel(record) {
    Modal.confirm({
      title: (
        <span>
          确定删除
          <span style={{ fontSize: 20, color: "red" }}>
            {getModelDisplayName(record)}
          </span>
          吗？
        </span>
      ),
      content: "删除后相关数据将全部删除不可恢复，请谨慎操作！",
      okText: "确定",
      cancelText: "取消",
      okButtonProps: {
        danger: true,
      },
      onOk: () => {
        return new Promise((resolve, reject) => {
          let param = buildDeleteModelKeyPayload(record);
          HttpTool.post(
            APILXD.modelDeleteKey,
            (code, msg, json) => {
              this.setState({ loading: false }, () => {
                message.success("操作成功");
                this._loadDataForNet(
                  getModelPageAfterDelete(this.state.page, this.items?.length)
                );
                resolve(true);
              });
            },
            (code, msg) => {
              message.error(msg);
              reject(false);
            },
            param
          );
        });
      },
    });
  }

  createKey(parentModelId, data) {
    const keyModal = Modal.confirm({
      title: data ? "编辑模型KEY" : "新增模型KEY",
      icon: null,
      width: 500,
      closable: true,
      content: (
        <div>
          <ProForm
            initialValues={data ? data : { weight: 1 }}
            submitter={{
              render: (props) => {
                const submitting = getModelSubmitterLoading(props);
                return (
                  <Flex style={{ width: "100%" }} justify="flex-end">
                    <Space size={16}>
                      <Button
                        type="default"
                        disabled={submitting}
                        onClick={() => {
                          keyModal.destroy();
                        }}
                      >
                        取消
                      </Button>
                      <Button
                        type="primary"
                        loading={submitting}
                        onClick={() => {
                          props.form?.submit();
                        }}
                      >
                        保存
                      </Button>
                    </Space>
                  </Flex>
                );
              },
            }}
            onFinish={(values) => {
              return new Promise((resolve, reject) => {
                let param = buildModelKeyEntityPayload(
                  parentModelId,
                  values,
                  data
                );
                HttpTool.post(
                  data ? APILXD.modelEditKeyEntity : APILXD.modelAddKeyEntity,
                  (code, msg, json) => {
                    message.success(data ? "修改成功" : "添加成功");
                    resolve(true);
                    keyModal.destroy();
                    this.keyTableRef?.reload?.();
                    this.refreshModelTokenSummary();
                  },
                  (code, msg) => {
                    message.error(msg);
                    reject(false);
                  },
                  param
                );
              });
            }}
          >
            <ProFormText
              label="地址"
              placeholder="请输入接口地址 (https://xxx)"
              name="endpoint"
              rules={[
                { required: true, message: "请输入地址" },
                {
                  pattern: /^https?:\/\/.+/,
                  message: "请输入正确的地址",
                },
              ]}
              fieldProps={{
                maxLength: 1000,
              }}
            />
            <ProFormText
              label="KEY"
              placeholder="请输入模型KEY"
              name="api_key"
              rules={[{ required: true, message: "请输入KEY" }]}
            />
            <ProFormDigit
              label="权重"
              placeholder="请输入权重"
              name="weight"
              rules={[{ required: true, message: "请填写权重" }]}
              fieldProps={{
                step: 1,
              }}
            />
          </ProForm>
        </div>
      ),
      footer: null,
    });
  }

  delModelKey(parentModelId, record) {
    Modal.confirm({
      title: (
        <span>
          确定删除
          <span style={{ fontSize: 20, color: "red" }}>
            {record?.index ?? "KEY"}
          </span>
          吗？
        </span>
      ),
      icon: null,
      closable: true,
      content: <div>删除后相关数据将全部删除不可恢复，请谨慎操作。</div>,
      okButtonProps: {
        danger: true,
      },
      onOk: () => {
        return new Promise((resolve, reject) => {
          let parms = {
            ...buildDeleteModelKeyEntityPayload(parentModelId, record),
          };
          HttpTool.post(
            APILXD.modelDelKeyEntity,
            (code, msg, json) => {
              message.success("删除成功");
              this.keyTableRef?.reload?.();
              this.refreshModelTokenSummary();
              resolve(true);
            },
            (code, msg) => {
              message.error(msg);
              reject(false);
            },
            parms
          );
        });
      },
    });
  }

  openKeyList(record) {
    const visibleColumnKeys = getModelKeyListColumnKeys();
    Modal.confirm({
      title: "KEY列表",
      width: "1000px",
      closable: true,
      icon: null,
      content: (
        <div style={{ width: "100%", height: 700 }}>
          <ProTable
            className={styles.modelKeyList}
            bordered={false}
            actionRef={(ref) => (this.keyTableRef = ref)}
            key="index"
            rowKey="index"
            style={{ height: 700 }}
            toolBarRender={() => (
              <Button
                type="primary"
                onClick={() => {
                  this.createKey(record._id, null);
                }}
              >
                新建KEY
              </Button>
            )}
            scroll={{
              y: 550,
            }}
            search={false}
            columns={[
              {
                title: "地址",
                dataIndex: "endpoint",
                key: "endpoint",
              },
              {
                title: "KEY",
                dataIndex: "api_key",
                key: "api_key",
              },
              {
                title: "权重",
                dataIndex: "weight",
                key: "weight",
                render: (text) => {
                  return <div>{text}</div>;
                },
              },
              {
                title: "已使用Tokens",
                dataIndex: "tokens",
                key: "tokens",
                render: (text) => {
                  return <div>{text ?? "-"}</div>;
                },
              },
              {
                title: "操作",
                key: "action",
                width: 160,
                render: (text, data, index) => {
                  return (
                    <Space>
                      <Button
                        size={"small"}
                        type={"primary"}
                        onClick={() => {
                          this.createKey(record?._id, data);
                        }}
                      >
                        编辑
                      </Button>
                      <Button
                        size={"small"}
                        type={"primary"}
                        danger={true}
                        onClick={() => {
                          this.delModelKey(record?._id, data);
                        }}
                      >
                        删除
                      </Button>
                    </Space>
                  );
                },
              },
            ].filter((column) => visibleColumnKeys.includes(column.key))}
            request={(params) => {
              return new Promise((resolve, reject) => {
                let myParams = buildModelKeyListPayload(record._id);
                HttpTool.post(
                  APILXD.modelGetKeyList,
                  (code, msg, json, option) => {
                    this.setState({ loading: false });
                    const list = Array.isArray(json) ? json : [];
                    resolve({
                      data: list,
                      success: true,
                      total: list.length,
                    });
                  },
                  (code, msg) => {
                    message.error(msg);
                    resolve({
                      data: [],
                      success: false,
                      total: 0,
                    });
                  },
                  myParams
                );
              });
            }}
          ></ProTable>
        </div>
      ),
      footer: null,
    });
  }
  /**
   * 忽略
   */
  ignoreState(record) {
    Modal.confirm({
      title: "提示",
      content: (
        <div>
          确定要<span style={{ color: "red", fontSize: "20px" }}>忽略</span>
          该举报信息吗？
        </div>
      ),

      okText: "确定",
      cancelText: "取消",
      okType: "danger",
      onOk: () => {
        let param = {
          reviewId: record._id,
        };
        let successCB = (code, msg, json, option) => {
          this.setState({ loading: false }, () => {
            message.success("操作成功");
            this._loadDataForNet();
          });
        };
        let failureCB = (code, msg) => {
          this.setState({ loading: false }, () => {
            message.error(msg);
          });
        };

        this.setState({ loading: true });

        HttpTool.post(APILXD.ignoreReport, successCB, failureCB, param);
      },
    });
  }

  // 停用、启用
  changeState(record) {
    const status = record.enableStatus;
    const statusDescription = isModelEnabled(status) ? "停用" : "启用";
    const userAccessDescription = isModelEnabled(status) ? "不可" : "可";

    const delistModal = Modal.confirm({
      title: (
        <div>
          确定{statusDescription}
          <span style={{ color: "blue", fontSize: "20px" }}>
            {getModelDisplayName(record)}
          </span>
          吗？
        </div>
      ),
      content: (
        <span>
          {statusDescription}后用户将
          {userAccessDescription}使用和修改，请谨慎操作。
        </span>
      ),
      okText: "确定",
      cancelText: "取消",
      onOk: () => {
        return new Promise((resolve, reject) => {
          let param = {
            _id: record._id,
            enableStatus: getNextModelEnableStatus(status),
          };
          let successCB = (code, msg, json, option) => {
            this.setState({ loading: false }, () => {
              message.success("操作成功");
              this._loadDataForNet();
              delistModal.destroy();
            });
            resolve(true);
          };
          let failureCB = (code, msg) => {
            this.setState({ loading: false }, () => {
              message.error(msg);
              reject(false);
            });
          };

          this.setState({ loading: true });

          HttpTool.post(
            APILXD.enableAiModelStatus,
            successCB,
            failureCB,
            param
          );
        });
      },
    });
  }
}
